// orderService.js - Business logic for order management

import { formatNumberSmart } from '@/utils/numberFormat';

export class OrderService {
    constructor(apiService) {
        this.apiService = apiService;
    }



    async loadOrders(params) {
        try {
            const response = await this.apiService.fetchData({
                endpoint: 'orders',
                params: this.buildOrderParams(params)
            });

            if (response && response.data) {
                return {
                    orders: response.data,
                    meta: {
                        currentPage: response.meta.current_page || 1,
                        totalPages: response.meta.total_pages || 1,
                        totalCount: response.meta.total_count || 0
                    }
                };
            }
            throw new Error('Unexpected API response structure');
        } catch (error) {
            console.error('Error loading orders:', error);
            throw error;
        }
    }

    buildOrderParams(params) {
        const apiParams = {
            page: params.currentPage,
            per_page: params.perPage
        };

        if (params.filters) {
            const { search, employee_id, status, priority, deliveryMethod, dateFrom, dateTo, site } = params.filters;
            if (search)         apiParams.q             = search;
            if (employee_id)    apiParams.employee_id   = employee_id;
            if (priority)       apiParams.priority      = priority;
            if (deliveryMethod) apiParams.delivery_method = deliveryMethod;
            if (dateFrom)       apiParams.date_from     = dateFrom;
            if (dateTo)         apiParams.date_to       = dateTo;
            if (site)           apiParams.pk_sb         = site;   // 'pk' | 'sb'

            // Kopf-Reiter (Chef 10.06.): Unfertige | ausgeliefert | Rechnungen |
            // Alle. Der explizit gewählte Status-Filter (Dropdown) gewinnt; sonst
            // bestimmt der Reiter den Status-Umfang.
            //   undelivered → alles vor "ausgeliefert" (Default-Ansicht)
            //   delivered   → nur ausgelieferte
            //   invoiced    → nur Rechnungen
            //   all         → wirklich alles (auch ausgeliefert/Rechnung/Archiv)
            const group = params.statusGroup;
            if (status) {
                apiParams.status = status;
            } else if (group === 'delivered') {
                apiParams.status = 'delivered';
            } else if (group === 'invoiced') {
                apiParams.status = 'invoice_issued';
            } else if (group === 'undelivered') {
                apiParams.undelivered = true;
            } else if (group === 'all') {
                // nichts ausblenden
            } else {
                // Default-/Legacy-Verhalten: "Rechnung raus" ausblenden.
                apiParams.hide_invoiced = true;
            }
        }

        if (params.sortField) {
            apiParams.sort      = params.sortField;
            apiParams.direction = params.sortDirection;
        }

        return apiParams;
    }

    async deleteOrder(orderId) {
        try {
            await this.apiService.fetchData({
                endpoint: `orders/${orderId}`,
                method: 'DELETE'
            });
            return true;
        } catch (error) {
            console.error('Error deleting order:', error);
            throw error;
        }
    }

    async loadCustomerDetails(customerId) {
        try {
            const customerResponse = await this.apiService.fetchData({
                endpoint: `customers/${customerId}`
            });

            const contactsResponse = await this.apiService.fetchData({
                endpoint: 'contacts',
                params: {
                    'q[customer_id_eq]': customerId
                }
            });

            return {
                customer: customerResponse.data || customerResponse,
                contacts: contactsResponse.data || []
            };
        } catch (error) {
            console.error('Error loading customer details:', error);
            throw error;
        }
    }
}

export class OrderFormatter {
    static getEmployeeName(employeeId, employeeList) {
        const employee = employeeList.find(m => m.id === employeeId);
        return employee ? employee.name : 'Unbekannt';
    }

    static getPaymentStatusClass(status) {
        const classes = {
            'deposit_required': 'payment-status-deposit-required',
            'deposit_booked': 'payment-status-deposit-booked',
            'cash_on_delivery': 'payment-status-cod',
            'cod_paid': 'payment-status-cod-paid',
            'invoice': 'payment-status-invoice',
            'invoice_paid': 'payment-status-paid'
        };
        return classes[status] || 'payment-status-default';
    }

    static getPaymentStatusText(status) {
        const texts = {
            'deposit_required': 'AZ',
            'deposit_booked': 'AZ✓',
            'cash_on_delivery': 'NN',
            'cod_paid': 'NN✓',
            'invoice': 'RE',
            'invoice_paid': 'RE✓'
        };
        return texts[status] || '?';
    }

    // Keyed by the Order#priority enum string (urgent/critical/normal/low).
    static getPriorityClass(priority) {
        const classes = {
            'urgent':   'priority-critical',
            'critical': 'priority-critical',
            'normal':   'priority-standard',
            'low':      'priority-low'
        };
        return classes[priority] || 'priority-default';
    }

    // Short P-codes for the narrow "P" column. P0 = priority not on record.
    static getPriorityText(priority) {
        const prioritaetMap = {
            'urgent':   'P1',
            'critical': 'P1',
            'normal':   'P2',
            'low':      'P3'
        }
        return prioritaetMap[priority] || 'PX'
    }

    static getStatusText(status, statusOptions) {
        const entry = statusOptions.find(opt => opt.value === status);
        return entry ? entry.text : status;
    }

    static getStatusClass(status, statusOptions) {
        const entry = statusOptions.find(opt => opt.value === status);
        return entry ? entry.class : 'status-default';
    }

    // Boss-Mail 2: Material-Status IMMER farbcodiert:
    //   keine Eingabe/Planung -> ROT, Bestellung/Zuweisung -> GELB,
    //   Zuteilung/Bereitstellung verspätet -> ORANGE, zugeteilt/vor Ort -> GRÜN.
    static getMaterialStatusClass(status) {
        const classes = {
            'available': 'material-status-available', // grün
            'ordered':   'material-status-ordered',   // gelb
            'pending':   'material-status-delayed'    // orange (verspätet)
        };
        return classes[status] || 'material-status-missing'; // rot: keine Eingabe
    }

    static getMaterialStatusText(status) {
        const texts = {
            'available': 'Vor Ort',
            'ordered':   'Bestellt',
            'pending':   'Verspätet'
        };
        return texts[status] || 'fehlt';
    }

    static formatDeliveryMethod(method) {
        const methods = {
            'DPD': 'DPD',
            'Spedition': 'Spedition',
            'Selbstabholer': 'Selbstabholer'
        };
        return methods[method] || method;
    }

    // Delegates to the shared smart formatter. Keeps free-text values like
    // "50x" or "ca. 1000" intact instead of turning them into "NaN".
    static formatNumber(number) {
        return formatNumberSmart(number);
    }


    static getDeliveryMethodLabel(method) {
        const labels = {
            '0': 'DPD',
            '1': 'Spedition',
            '2': 'Selbstabholer',
            '3': 'Kurier',
            '4': 'Post',
            '5': 'UPS',
            '6': 'Eigen'
        };

        const methodStr = String(method);
        return labels[methodStr] || `Unbekannt (${method})`;
    }

    static formatDate(dateString) {
        if (!dateString) return '';

        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');

        return `${day}.${month}.${year} ${hours}:${minutes}`;
    }

    // Boss-Mail 2: Lieferdatum deutsch TT.MM.JJ (2-stelliges Jahr).
    static formatDateDE(dateString) {
        if (!dateString) return '';
        const d = new Date(dateString);
        if (isNaN(d.getTime())) return '';
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yy = String(d.getFullYear()).slice(-2);
        return `${dd}.${mm}.${yy}`;
    }

    // Uhrzeit separat (unter dem Datum); echte 00:00 wird ausgeblendet.
    static formatTime(dateString) {
        if (!dateString) return '';
        const d = new Date(dateString);
        if (isNaN(d.getTime())) return '';
        const hh = String(d.getHours()).padStart(2, '0');
        const mi = String(d.getMinutes()).padStart(2, '0');
        return (hh === '00' && mi === '00') ? '' : `${hh}:${mi}`;
    }

    // Boss-Mail 2: Format "12,0 x 34,0" (immer in cm, Einheit NICHT anzeigen,
    // Komma als Dezimaltrenner, eine Nachkommastelle).
    static formatSheetFormat(value) {
        if (value === null || value === undefined || value === '') return '';
        let s = String(value).trim().replace(/\s*(cm|mm)\s*$/i, '');
        const fmt = (n) => {
            const num = parseFloat(String(n).replace(',', '.'));
            return isNaN(num) ? String(n).trim() : num.toFixed(1).replace('.', ',');
        };
        const parts = s.split(/\s*[x×]\s*/i);
        return parts.length === 2 ? `${fmt(parts[0])} x ${fmt(parts[1])}` : s.replace('.', ',');
    }
}

export const ORDER_CONSTANTS = {
    // Employee list is now loaded from API (/api/v1/users)
    EMPLOYEE_LIST: [],

    STATUS_OPTIONS: [
        { value: 'order_received',     text: 'Auftrag erteilt',       class: 'status-ordered',    color: '#FF0000', textColor: '#000' },
        { value: 'prepress_complete',  text: 'Druckfreigabe erteilt', class: 'status-prepress',   color: '#FFCC00', textColor: '#000' },
        { value: 'plates_complete',    text: 'Druckplatten fertig',   class: 'status-plates',     color: '#FFFF99', textColor: '#000' },
        { value: 'printing_complete',  text: 'Druck fertig',          class: 'status-printing',   color: '#00FF00', textColor: '#000' },
        { value: 'processing_complete',text: 'WTV fertig',            class: 'status-processing', color: '#CCFFFF', textColor: '#000' },
        { value: 'shipping_complete',  text: 'Versand fertig',        class: 'status-shipping',   color: '#7030A0', textColor: '#fff' },
        { value: 'delivered',          text: 'ausgeliefert',          class: 'status-delivered',  color: '#3366FF', textColor: '#fff' },
        { value: 'invoice_issued',     text: 'Rechnung raus',         class: 'status-invoiced',   color: '#C0C0C0', textColor: '#000' },
        { value: 'archived',           text: 'Archiviert',            class: 'status-archived',   color: '#E0E0E0', textColor: '#333' }
    ],

    DELIVERY_METHODS: [
        { value: 'DPD', text: 'DPD' },
        { value: 'Spedition', text: 'Spedition' },
        { value: 'Selbstabholer', text: 'Selbstabholer' }
    ],

    // A-5 — Raster-Ausgabegröße. Handauswahl, Standard 70er. Mirrors
    // Order::RASTER_OPTIONS / RASTER_DEFAULT. Not exhaustive by design: the
    // editor keeps any other stored value selectable (Prinance LE 38 is free
    // text), so switching to a dropdown never silently drops data.
    RASTER_OPTIONS: [
        { value: '60', label: '60er' },
        { value: '70', label: '70er' },
        { value: '80', label: '80er' }
    ],
    RASTER_DEFAULT: '70',

    // #17 — Erstplanung. Entwurf = grau auf der Kap.Pl., bis der Sachbearbeiter
    // die importierten Termine bestätigt hat.
    PLANNING_STATE_OPTIONS: [
        { value: 'draft',     label: 'Entwurf (grau)' },
        { value: 'confirmed', label: 'Bestätigt' }
    ],

    // Filter options for the Auftrag list. Values are the Order#priority enum
    // keys; 'none' is a sentinel for "priority not on record" (backend maps it
    // to priority IS NULL). `critical` is intentionally not offered (it still
    // displays, but folds into the high-prio bucket for filtering).
    PRIORITY_OPTIONS: [
        { value: 'none',   label: 'PX - Unbekannt' },
        { value: 'urgent', label: 'P1 - Dringend' },
        { value: 'normal', label: 'P2 - Normal' },
        { value: 'low',    label: 'P3 - Niedrig' }
    ],

    // Integer-keyed options matching the DB `delivery_method` column (Delivery#delivery_method_text).
    DELIVERY_METHOD_OPTIONS: [
        { value: 0, text: 'DPD' },
        { value: 1, text: 'Spedition' },
        { value: 2, text: 'Selbstabholer' },
        { value: 3, text: 'Kurier' },
        { value: 4, text: 'Post' },
        { value: 5, text: 'UPS' },
        { value: 6, text: 'Eigen' }
    ],

    // Boss-Mail 2: Spaltenreihenfolge angepasst. 'P'->'Prio', Versandart vor
    // Gewicht, Sachbearbeiter vor Material-Status, 'Ort' bereits entfernt.
    // `align`: Standard mittig; Kunde/Beschreibung/Papier links; Auflage/Gewicht rechts.
    TABLE_COLUMNS: [
        {key: 'actions', label: 'Aktionen', sortable: false, align: 'center'},
        {key: 'order_number', label: 'Auftrag #\nRechnung #', sortable: true, align: 'center'},
        {key: 'priority', label: 'Prio', sortable: true, align: 'center'},
        {key: 'status', label: 'Status', sortable: true, align: 'center'},
        {key: 'delivery_date', label: 'Lieferdatum', sortable: true, align: 'center'},
        {key: 'customer_name', label: 'Kunde', sortable: true, align: 'left'},
        {key: 'description', label: 'Beschreibung', sortable: true, align: 'left'},
        {key: 'print_run', label: 'Auflage', sortable: true, align: 'right'},
        {key: 'format', label: 'Format', sortable: true, align: 'center'},
        {key: 'page_count', label: 'Seiten', sortable: true, align: 'center'},
        {key: 'print_format', label: 'Druckformat', sortable: true, align: 'center'},
        {key: 'color', label: 'Farbe', sortable: true, align: 'center'},
        {key: 'employee_id', label: 'Sachbearbeiter', sortable: true, align: 'center'},
        {key: 'material_status', label: 'Material\nStatus', sortable: true, align: 'center'},
        {key: 'paper_type', label: 'Papier', sortable: true, align: 'left'},
        {key: 'delivery_method', label: 'Versandart', sortable: true, align: 'center'},
        {key: 'paper_weight', label: 'Gewicht', sortable: true, align: 'right'},
        {key: 'print_date', label: 'Druck\nDatum', sortable: true, align: 'center'},
    ]
};
// A-5 — Optionen für die Rastergröße inkl. Alt-Wert.
//
// Die Spalte ist bewusst Freitext (Prinance LE 38 liefert, was es will). Ein
// gespeicherter Wert außerhalb der Standardliste muss wählbar bleiben, sonst
// würde ihn das Dropdown beim ersten Speichern stillschweigend ersetzen.
export function rasterOptionsFor(currentValue) {
    const opts = ORDER_CONSTANTS.RASTER_OPTIONS.map(o => ({...o}));
    if (currentValue == null || currentValue === '') return opts;
    if (opts.some(o => String(o.value) === String(currentValue))) return opts;
    return [{value: currentValue, label: `${currentValue} (Alt)`}, ...opts];
}
